import React from "react";
import usePremiumPlan from "../hooks/usePremiumPlan";
import { activatePremiumPlan } from "../utils/premiumPlan";
import "../styles/Pricing.css";

function Pricing({ user }) {
  const isPremium = usePremiumPlan(user?.id);
  const activatedPlanId = isPremium ? "growth" : null;

  const handleClaimOffer = (planId) => {
    if (planId !== "growth" || isPremium || !user?.id) return;
    activatePremiumPlan(user.id, planId);
  };

  const plans = [
    {
      id: "starter",
      title: "1 Month",
      badge: "Fast start",
      price: "Rs 149",
      oldPrice: "Rs 299",
      cadence: "/month",
      cta: "Choose Starter",
      disableCta: true,
      bullets: [
        "Unlimited chats and media sharing",
        "Priority Kannada + English support",
        "Daily cloud backup for 30 days",
      ],
      highlight: false,
    },
    {
      id: "growth",
      title: "6 Months",
      badge: "Most Popular",
      price: "Rs 599",
      oldPrice: "Rs 1,194",
      cadence: "/6 months",
      cta: "Claim Launch Offer",
      disableCta: false,
      bullets: [
        "Everything in 1 Month plan",
        "Group call quality boost + AI noise cleanup",
        "Business profile badge and lead tools",
      ],
      highlight: true,
    },
    {
      id: "pro",
      title: "1 Year",
      badge: "Best savings",
      price: "Rs 999",
      oldPrice: "Rs 2,388",
      cadence: "/year",
      cta: "Upgrade to Pro",
      disableCta: true,
      bullets: [
        "Everything in 6 Month plan",
        "Advanced admin controls + audit history",
        "Premium onboarding and growth playbook",
      ],
      highlight: false,
    },
  ];

  const policies = [
    {
      title: "Straightforward billing",
      text: "All plan details and taxes are shown before payment. No hidden surprises.",
    },
    {
      title: "Cancel anytime",
      text: "You can stop auto-renewal whenever you want from account settings.",
    },
    {
      title: "Secure payments",
      text: "Trusted payment gateways with encrypted transactions and OTP checks.",
    },
    {
      title: "Fair usage",
      text: "High limits for normal usage with safeguards to block spam behavior.",
    },
  ];

  return (
    <section className="pricing-page">
      <div className="pricing-shell">
        <header className="pricing-hero">
          <p className="pricing-kicker">Transparent pricing</p>
          <div className="launch-offer-banner" aria-label="Launch offer">
            <span className="offer-tag">Launching Offer</span>
            <strong>100% OFF</strong>
            <span className="offer-note">Limited time access for everyone</span>
          </div>
          <h1>Pick a plan that grows with your chat community</h1>
          <p className="pricing-subtext">
            Simple monthly options and smart long-term savings for creators,
            teams, and businesses using ChatApp.
          </p>
          <div className="pricing-pills">
            <span>Instant activation</span>
            <span>Secure payments</span>
            <span>Cancel anytime</span>
          </div>
        </header>

        <div className="pricing-cards" role="list" aria-label="Pricing plans">
          {plans.map((plan) => (
            <article
              key={plan.id}
              className={`plan-card ${plan.highlight ? "featured" : ""}`}
              role="listitem"
            >
              <div className="plan-head">
                <h3>{plan.title}</h3>
                <span className="plan-badge">{plan.badge}</span>
              </div>
              <div className="plan-price-wrap">
                <p className="plan-price">{plan.price}</p>
                <p className="plan-meta">
                  <span className="old-price">{plan.oldPrice}</span> {plan.cadence}
                </p>
              </div>
              <ul className="plan-features">
                {plan.bullets.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`plan-cta${plan.disableCta ? " disabled" : ""}${
                  activatedPlanId === plan.id ? " activated" : ""
                }`}
                disabled={plan.disableCta || activatedPlanId === plan.id}
                onClick={() => handleClaimOffer(plan.id)}
              >
                {activatedPlanId === plan.id ? "Plan Activated" : plan.cta}
              </button>
            </article>
          ))}
        </div>

        <section className="pricing-policy" aria-label="Pricing policies">
          <h2>Pricing policies</h2>
          <p>
            We keep pricing clear and predictable so your team can focus on
            conversations, support, and growth.
          </p>
          <div className="policy-grid">
            {policies.map((item) => (
              <article key={item.title} className="policy-card">
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="pricing-support">
          <h3>Need help choosing the right plan?</h3>
          <p>
            Our onboarding team can recommend the best subscription based on your
            team size and usage.
          </p>
          <button type="button" className="support-btn">
            Contact support
          </button>
        </section>
      </div>
    </section>
  );
}

export default Pricing;
